import {
  IconChevronLeft,
  IconChevronRight,
} from '@leland-dev/leland-ui-library';
import Link, { type LinkProps } from 'next/link';
import type React from 'react';
import {
  type FC,
  type MouseEventHandler,
  type RefObject,
  type SVGProps,
} from 'react';
import { useCallback, useMemo } from 'react';
import { type UrlObject } from 'url';

import { getPagination } from '../../utils/pagination';

enum PaginationDirection {
  PREV,
  NEXT,
}

const PAGINATION_DIRECTION_ICON: Record<
  PaginationDirection,
  FC<SVGProps<SVGSVGElement>>
> = {
  [PaginationDirection.PREV]: IconChevronLeft,
  [PaginationDirection.NEXT]: IconChevronRight,
};

interface PaginationProps {
  totalElements: number;
  currentPage: number;
  pageSize: number;
  buildUrl: (page: number) => string | UrlObject;
  scrollRoot?: RefObject<Nullable<HTMLElement>>;
  maxPageItems: 3 | 5 | 7;
  useShallowRouting?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  totalElements,
  currentPage,
  pageSize,
  buildUrl,
  scrollRoot,
  maxPageItems,
  useShallowRouting = true,
}) => {
  const pagination = useMemo(
    () => getPagination(totalElements, currentPage, pageSize, maxPageItems),
    [totalElements, currentPage, pageSize, maxPageItems],
  );

  const hasScrollRoot = !!scrollRoot;

  const generateLinkProps = useCallback(
    (page: number): LinkProps => ({
      href: buildUrl(page),
      shallow: useShallowRouting,
      scroll: !hasScrollRoot,
    }),
    [buildUrl, hasScrollRoot, useShallowRouting],
  );

  const onNavigate = useCallback(() => {
    if (scrollRoot?.current) {
      scrollRoot.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [scrollRoot]);

  if (pagination.totalPages < 1) {
    return null;
  }

  return (
    <ul className="flex items-center gap-4">
      <PaginationArrowItem
        paginationDirection={PaginationDirection.PREV}
        onClick={onNavigate}
        disabled={currentPage === 1}
        linkProps={
          currentPage === 1 ? null : generateLinkProps(currentPage - 1)
        }
      />
      {pagination.pages.map((page) => (
        <PaginationNumberItem
          key={page}
          page={page}
          onClick={onNavigate}
          isCurrentPage={currentPage === page}
          {...generateLinkProps(page)}
        />
      ))}
      <PaginationArrowItem
        paginationDirection={PaginationDirection.NEXT}
        onClick={onNavigate}
        disabled={currentPage === pagination.totalPages}
        linkProps={
          currentPage === pagination.totalPages
            ? null
            : generateLinkProps(currentPage + 1)
        }
      />
    </ul>
  );
};

interface PaginationArrowItemProps {
  paginationDirection: PaginationDirection;
  onClick: MouseEventHandler;
  disabled: boolean;
  linkProps: Possible<LinkProps>;
}

const PaginationArrowItem: React.FC<PaginationArrowItemProps> = ({
  paginationDirection,
  onClick,
  disabled,
  linkProps,
}) => {
  const PaginationIcon = PAGINATION_DIRECTION_ICON[paginationDirection];
  return (
    <li>
      {linkProps ? (
        <Link
          {...linkProps}
          className={`flex size-11 items-center justify-center rounded-lg border border-leland-gray-stroke ${
            disabled
              ? 'pointer-events-none cursor-not-allowed text-gray-400'
              : 'hover:bg-leland-gray-hover'
          }`}
          onClick={disabled ? undefined : onClick}
          id={`pagination:${paginationDirection}`}
        >
          <PaginationIcon className="size-4" />
        </Link>
      ) : (
        <button
          className="flex size-11 cursor-not-allowed items-center justify-center rounded-lg border border-leland-gray-stroke text-gray-400"
          disabled={disabled}
          id={`pagination:${paginationDirection}`}
        >
          <PaginationIcon className="size-4" />
        </button>
      )}
    </li>
  );
};

type PaginationNumberItemProps = LinkProps & {
  page: number;
  isCurrentPage: boolean;
  onClick: MouseEventHandler;
};

const PaginationNumberItem: React.FC<PaginationNumberItemProps> = ({
  page,
  isCurrentPage,
  onClick,
  ...linkProps
}) => (
  <li className={`flex items-center justify-center`}>
    <Link
      {...linkProps}
      className={`flex size-11 items-center justify-center rounded-lg p-3 ${
        isCurrentPage
          ? 'pointer-events-none cursor-not-allowed bg-leland-primary font-medium text-white'
          : 'hover:bg-leland-gray-hover'
      }`}
      onClick={isCurrentPage ? undefined : onClick}
      id="pagination--number_click"
    >
      {page}
    </Link>
  </li>
);

export default Pagination;
